import SettingsFiscalCreateCertificateModal from '@/components/Settings/Fiscal/SettingsFiscalCreateCertificateModal';
import { FloatingFormItem, Group } from '@/components/_common';
import { useInvoiceSettingCertificates } from '@/core/domain/invoice/certificate/Certificate.hooks';
import {
  InvoiceSpecialTaxRegime,
  InvoiceTaxRegime,
} from '@/core/domain/invoice/invoices/invoices.enums';
import { InvoiceSettingEnvironment } from '@/core/domain/invoice/settings/Settings.enums';
import { CompanyInvoiceSettingsForm } from '@/core/domain/invoice/settings/Settings.types';
import { useDefaultEmptyFieldError, useDisclosure } from '@/hooks';
import {
  Alert,
  Button,
  Checkbox,
  Col,
  Form,
  Radio,
  Row,
  Select,
  Typography,
} from 'antd';
import { FormInstance } from 'antd/es/form/Form';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { useEffect } from 'react';
import { FaPlus } from 'react-icons/fa';
import { useHistory } from 'react-router-dom';
import AuthorizationDownloadXmlForm from './AuthorizationDownloadXmlForm';
import { RegistrationsDefaultCode } from '@/types/Register';

interface Props {
  invoiceSettingsId?: number;
  form: FormInstance<CompanyInvoiceSettingsForm>;
}

const InvoiceDetails = ({ form, invoiceSettingsId }: Props) => {
  const fieldRequired = useDefaultEmptyFieldError();

  const history = useHistory();

  const issueNfc = Form.useWatch('issueNfc', form);
  const issueNfe = Form.useWatch('issueNfe', form);
  const issueNfs = Form.useWatch('issueNfs', form);

  const [modalCertificateOpen, modalCertificateAction] = useDisclosure();

  const showNFMessage =
    !issueNfc && !issueNfe && !issueNfs && 'Selecione uma ou mais opções';

  useEffect(() => {
    form.setFieldValue('showNFMessage', !issueNfc && !issueNfe && !issueNfs);
  }, [!issueNfc, issueNfe, issueNfs]);

  const { data: certificates, isLoading } = useInvoiceSettingCertificates();

  const specialTaxSystemOptions = [
    {
      value: InvoiceSpecialTaxRegime.NO_SPECIAL_TAX_REGIME,
      label: 'Sem regime tributário especial',
    },
    {
      value: InvoiceSpecialTaxRegime.MUNICIPAL_MICRO_ENTERPRISE,
      label: 'Micro empresa municipal',
    },
    {
      value: InvoiceSpecialTaxRegime.ESTIMATED,
      label: 'Estimativa',
    },
    {
      value: InvoiceSpecialTaxRegime.PROFESSIONAL_SOCIETY,
      label: 'Sociedade de profissionais',
    },
    {
      value: InvoiceSpecialTaxRegime.COOPERATIVE,
      label: 'cooperativa',
    },
    {
      value: InvoiceSpecialTaxRegime.INDIVIDUAL_MICROENTREPRENEUR_MEI,
      label: 'Microempresário individual - MEI',
    },
    {
      value: InvoiceSpecialTaxRegime.SMALL_OR_MICROENTERPRISE_ME_EPP,
      label: 'Microempresa ou pequeno porte - ME EPP',
    },
    {
      value: InvoiceSpecialTaxRegime.REAL_PROFIT,
      label: 'Lucro real',
    },
    {
      value: InvoiceSpecialTaxRegime.PRESUMED_PROFIT,
      label: 'Lucro presumido',
    },
    {
      value: InvoiceSpecialTaxRegime.NORMAL_TAXATION,
      label: 'Tributação normal',
    },
    {
      value: InvoiceSpecialTaxRegime.NATIONAL_SIMPLES_WITH_EXCESS_OF_SUBLIMIT,
      label: 'Simples nacional com excesso do sublimite',
    },
    {
      value: InvoiceSpecialTaxRegime.LIMITED_LIABILITY_COMPANY,
      label: 'Empresa de responsabilidade limitada',
    },
  ];

  return (
    <Row gutter={[16, 16]}>
      <Col span={24} md={8}>
        <FloatingFormItem
          name="environment"
          label="Ambiente"
          rules={[fieldRequired]}
        >
          <Select
            options={[
              {
                value: InvoiceSettingEnvironment.PRODUCTION,
                label: 'Produção',
              },
              {
                value: InvoiceSettingEnvironment.STAGING,
                label: 'Homologação',
              },
            ]}
          />
        </FloatingFormItem>
      </Col>

      <Col span={24}>
        <Typography.Title level={4}>
          Tipos de notas <span style={{ color: 'red' }}>*</span>
        </Typography.Title>

        <Form.Item hidden name="showNFMessage" valuePropName="checked">
          <Checkbox />
        </Form.Item>

        <Row gutter={[16, 16]}>
          <Col>
            <FloatingFormItem
              valuePropName="checked"
              name="issueNfe"
              initialValue={false}
            >
              <Checkbox>Habilitar NF-e</Checkbox>
            </FloatingFormItem>
          </Col>
          <Col>
            <FloatingFormItem
              valuePropName="checked"
              name="issueNfc"
              initialValue={false}
            >
              <Checkbox>Habilitar NFC-e</Checkbox>
            </FloatingFormItem>
          </Col>
          <Col>
            <FloatingFormItem
              valuePropName="checked"
              name="issueNfs"
              initialValue={false}
            >
              <Checkbox>Habilitar NFS-e</Checkbox>
            </FloatingFormItem>
          </Col>
        </Row>

        {!!showNFMessage && (
          <span style={{ color: 'red' }}>{showNFMessage}</span>
        )}
      </Col>

      <Col span={24}>
        <Typography.Title level={4}>Dados gerais</Typography.Title>

        <Row gutter={[16, 16]}>
          <Col span={24} md={12} lg={12} xxl={12}>
            <FloatingFormItem
              name="specialTaxRegime"
              label="Regime Tributário Especial"
              rules={[fieldRequired]}
            >
              <Select options={specialTaxSystemOptions} />
            </FloatingFormItem>
          </Col>

          <Col span={24}>
            <FloatingFormItem
              valuePropName="checked"
              name="usePartWeightForVolume"
            >
              <Checkbox>Recuperação peso da peça</Checkbox>
            </FloatingFormItem>
          </Col>

          <Col span={24}>
            <Typography.Title level={4}>
              Listagem de notas fiscais
            </Typography.Title>

            <Row gutter={[16, 16]}>
              <Col>
                <FloatingFormItem
                  valuePropName="checked"
                  name="showValueTotalInvoiceInListing"
                >
                  <Checkbox>Mostrar valor total das notas fiscais</Checkbox>
                </FloatingFormItem>
              </Col>
            </Row>
          </Col>

          <Col span={24}>
            <Typography.Title level={4}>Dados fiscais</Typography.Title>

            <Row gutter={[16, 16]}>
              <Col>
                <FloatingFormItem
                  valuePropName="checked"
                  name="simpleNational"
                  initialValue={false}
                  getValueFromEvent={(e: CheckboxChangeEvent) => {
                    if (e.target.checked) {
                      form.setFieldValue(
                        'taxRegime',
                        InvoiceTaxRegime.SIMPLES_NATIONAL,
                      );
                    } else {
                      form.setFieldValue('taxRegime', undefined);
                    }

                    return e.target.checked;
                  }}
                >
                  <Checkbox>Simples nacional</Checkbox>
                </FloatingFormItem>
              </Col>

              <Col>
                <FloatingFormItem
                  valuePropName="checked"
                  name="culturalIncentive"
                >
                  <Checkbox>Incentivo cultural</Checkbox>
                </FloatingFormItem>
              </Col>

              <Col>
                <FloatingFormItem valuePropName="checked" name="taxIncentive">
                  <Checkbox>Incentivo fiscal</Checkbox>
                </FloatingFormItem>
              </Col>

              <Col>
                <FloatingFormItem
                  valuePropName="checked"
                  name="deductIcmsValueBaseCalculationPisCofins"
                >
                  <Checkbox>
                    Deduzir valor do icms informado na nota da base de cálculo
                    do PIS/COFINS
                  </Checkbox>
                </FloatingFormItem>
              </Col>
            </Row>
          </Col>

          <Col span={24}>
            <Typography.Title level={4}>Certificado digital</Typography.Title>

            {!certificates?.length && (
              <Alert
                style={{ marginBottom: 24 }}
                type="warning"
                description={
                  <>
                    <div style={{ marginBottom: 16 }}>
                      Não há certificado cadastrado, é necessário ao menos um
                      certificado para configurar a NF da empresa.
                    </div>

                    <Group gap={24}>
                      <Button
                        type="primary"
                        onClick={modalCertificateAction.open}
                      >
                        <Group>
                          <FaPlus size={14} />
                          <span>Adicionar certificado</span>
                        </Group>
                      </Button>

                      <Button
                        type="link"
                        onClick={() => history.push('/app/settings/fiscal')}
                      >
                        Ir para configuração de certificados
                      </Button>
                    </Group>
                  </>
                }
              />
            )}

            <Row gutter={[16, 16]}>
              <Col span={24} md={12}>
                <FloatingFormItem
                  label="Certificado"
                  name="certificateId"
                  rules={[fieldRequired]}
                >
                  <Select
                    loading={isLoading}
                    options={
                      certificates
                        ? certificates.map((item) => ({
                            value: item.id,
                            label: item.name,
                          }))
                        : []
                    }
                  />
                </FloatingFormItem>

                <SettingsFiscalCreateCertificateModal
                  open={modalCertificateOpen}
                  onCancel={modalCertificateAction.close}
                />
              </Col>
            </Row>
          </Col>
        </Row>
      </Col>

      <Col span={24}>
        <Typography.Title level={4}>Código padrão</Typography.Title>

        <Row gutter={[16, 16]}>
          <Col span={24} md={12} lg={12} xxl={12}>
            <Form.Item name="defaultPartCodeDanfe">
              <Radio.Group optionType="button" buttonStyle="solid" size="large">
                <Radio value={RegistrationsDefaultCode.manufacturerCode}>
                  Cód. fabricante
                </Radio>
                <Radio value={RegistrationsDefaultCode.barCode}>
                  Cód. barras
                </Radio>
                <Radio value={RegistrationsDefaultCode.internalCode}>
                  Cód. interno
                </Radio>
                <Radio value={RegistrationsDefaultCode.companyCode}>
                  Cód. empresa
                </Radio>
              </Radio.Group>
            </Form.Item>
          </Col>
        </Row>
      </Col>

      <Col span={24}>
        <Typography.Title level={4}>
          Autorização para baixar xml
        </Typography.Title>

        <Row gutter={[16, 16]}>
          <Col span={24}>
            <AuthorizationDownloadXmlForm
              invoiceSettingsId={invoiceSettingsId}
            />
          </Col>
        </Row>
      </Col>
    </Row>
  );
};

export default InvoiceDetails;
